import {Component, OnDestroy, OnInit, Input} from '@angular/core';
import {Observable} from "rxjs";
import {take} from "rxjs/operators";
import {Centre} from "../../../interfaces/Centres";
import {CentreService} from "../../../services/centre.service";
import {MatTableDataSource} from "@angular/material/table";
import {CounselingService} from "../../../modules/counseling/counseling.service";
import {Subject} from "rxjs/internal/Subject";
import {Employee} from "../../../interfaces/Employee";
import {LoadingService} from "../../../services/loading.service";
import {EmployeeService} from "../../../services/employee.service";
import {MatSelectChange} from "@angular/material/select";
import {
  CounsellingSessionReviewService
} from "../../../modules/counseling/components/CounsellingSessionReview/counselling-session-review.service";

export interface MissingSessionsLog {
  reason: 'Empty Name (→ Krisha Shah)' | 'Excluded Name (→ Krisha Shah)' | 'Unmatched (Active Mode)';
  counsellorName: string;
  normalizedKey: string;
  individual: number;
  group: number;
  followUp: number;
  centreCode?: string;
  docCount?: number;
}

export interface MissingSessionsReport {
  rawTotals:  { individual: number; group: number; followUp: number; total: number };
  cardTotals: { individual: number; group: number; followUp: number; total: number };
  diff:       { individual: number; group: number; followUp: number; total: number };
  logs: MissingSessionsLog[];
  generatedAt: Date;
}

export interface Counsellor  {
  profileImages: string;
  userName: string;
  designation: string;
  centre: string[];
  sessionCount: number;
  sessionObserve: number;
  individualCount: number;
  followUpCount: number;
  assessmentCount: number;
  joiningDate?: any;   // raw value from Firestore (Timestamp | Date | string | null)
  disabled?: boolean;
  expanded?: boolean;
  isSynthetic?: boolean;
}

@Component({
  selector: 'app-mis-counselling',
  templateUrl: './mis-counselling.component.html',
  styleUrls: ['./mis-counselling.component.scss']
})
export class MisCounsellingComponent implements OnInit, OnDestroy {

  @Input() selectedCentreCode!: string;
  @Input() isNestedComponent: boolean = false;

  allUsers: Employee[] = [];
  /** Snapshot of the employee-list counsellors — never modified after loadUsersAsync() */
  private baseUsers: Counsellor[] = [];
  counsellorColumns: string[] = [
    'no',
    'photo',
    'userName',
    'joiningDate',
    'centre',
    'totalSessionCount',
    'SessionObservedCount'
  ];
  dataSource = new MatTableDataSource<Employee>([]);
  selectedCentre = '';
  centres$: Observable<Centre[]>;
  allowedCentres$: Observable<Centre[]>;
  destroyed$ = new Subject();
  totalUnits = 0;

  counsellorsType: 'Active' | 'All' = 'Active';

  /** Active date range — null means "All Time" */
  startDate: Date | null = null;
  endDate: Date | null = null;

  /** Debug / discrepancy report populated after each search() */
  debugReport: MissingSessionsReport | null = null;
  showDebugButton=false;
  showDebugPanel = false;

  /** Human-readable label shown above the cards */
  get dateRangeLabel(): string {
    if (!this.startDate && !this.endDate) return 'All Time';
    const fmt = (d: Date) => d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    if (this.startDate && this.endDate) return `${fmt(this.startDate)} – ${fmt(this.endDate)}`;
    if (this.startDate) return `From ${fmt(this.startDate)}`;
    return `Up to ${fmt(this.endDate!)}`;
  }

  // ─── Table-level totals (computed from counsellingMonthlySummary) ───
  tableGroupTotal = 0;
  tableIndividualTotal = 0;
  tableFollowUpTotal = 0;
  tableObservedTotal = 0;
  tableAssessmentTotal = 0;

  /**
   * ✅ Name alias map: maps variant/alternate names → canonical (primary) name.
   */
  private readonly nameAliases: { [alias: string]: string } = {
    'krisha': 'krisha shah',
    'vaishali': 'vaishali nanda',
    'ankita ajay shelar': 'ankita shelar',
  };

  /**
   * Normalize a counsellor name: lowercase, trim, and resolve aliases to the canonical name.
   */
  private normalizeName(name: string): string {
    const key = (name || '').toLowerCase().trim();
    return this.nameAliases[key] || key;
  }

  constructor(
    private centerService: CentreService,
    private counselingService: CounselingService,
    public loadingService: LoadingService,
    private employeeService: EmployeeService,
    private counsellingSessionReviewService: CounsellingSessionReviewService,
  ) {
    this.centres$ = this.centerService.getAllCentres();
    this.allowedCentres$ = this.centerService.getAllCentres();
  }

  ngOnInit() {
    this.search();
  }

  onSearch() {
    this.search();
  }

  public onClear(): void {
    this.counsellorsType = 'Active';
    this.selectedCentre = '';
    this.startDate = null;
    this.endDate = null;
    this.allUsers = [];
    this.dataSource.data = [];
    this.tableGroupTotal = 0;
    this.tableIndividualTotal = 0;
    this.tableFollowUpTotal = 0;
    this.tableObservedTotal = 0;
    this.tableAssessmentTotal = 0;
    this.search();
  }



  /**
   * Loads counsellors as a Promise (matching mis-education pattern)
   */
  private async loadUsersAsync(): Promise<Counsellor[]> {
    const counsellors$ = this.counsellorsType === 'Active'
      ? this.employeeService.getCounsellers()
      : this.employeeService.getAllCounsellors();

    return new Promise((resolve, reject) => {
      counsellors$.pipe(take(1)).subscribe({
        next: async (res: Employee[]) => {
          if (!res || !Array.isArray(res) || res.length === 0) {
            resolve([]);
            return;
          }

          const counsellorsWithMissingCentres: any[] = [];
          const counsellors: Counsellor[] = [];

          for (const u of res) {
            if (!u) continue;
            // When "All" is selected, don't filter out disabled
            if (this.counsellorsType === 'Active' && u.disabled === true) continue;

            let centres: string[] = u.centre ? Object.values(u.centre) : [];

            // ✅ If no centres assigned, track for later processing
            if (centres.length === 0) {
              counsellorsWithMissingCentres.push(u);
              continue;
            }

            counsellors.push({
              profileImages: u.profileImages || 'assets/img/user-avatar.png',
              userName: u.userName || 'Unknown',
              designation: u.jobtitle || 'Counsellor',
              centre: centres,
              disabled: u.disabled ?? false,
              joiningDate: u.joiningDate ?? null,
              sessionCount: 0,
              sessionObserve: 0,
              individualCount: 0,
              followUpCount: 0,
              assessmentCount: 0,
              isSynthetic: false
            } as Counsellor);
          }

          // ✅ OPTIMIZED: Fetch centres from sessions for counsellors with missing centres
          if (counsellorsWithMissingCentres.length > 0) {
            console.log(`📍 Fetching centres from sessions for ${counsellorsWithMissingCentres.length} counsellors...`);

            try {
              // Derived from counsellingMonthlySummary rather than by scanning every
              // session: a summary doc is keyed {counsellorName}_{yearMonth}_{centreCode},
              // so the mapping is already carried by the ~50-200 docs that back this table.
              // Fetched without a date range on purpose — the scan this replaces was
              // all-time, so a counsellor whose sessions fall outside the current filter
              // must still resolve a centre.
              const centreSummaryDocs = await this.counselingService
                .getSummaryForDateRange([], null, null);

              // Build a map: counsellorName -> Set of centreCodes
              const counsellorCentreMap = new Map<string, Set<string>>();
              centreSummaryDocs?.forEach((doc: any) => {
                const submittedBy = this.normalizeName(doc.counsellorName);
                // 'All' is the fallback bucket for group sessions that carry no root
                // centreCode — it is not a real centre, so it must not become one here.
                if (submittedBy && doc.centreCode && doc.centreCode !== 'All') {
                  if (!counsellorCentreMap.has(submittedBy)) {
                    counsellorCentreMap.set(submittedBy, new Set<string>());
                  }
                  counsellorCentreMap.get(submittedBy)!.add(doc.centreCode);
                }
              });

              // Get all centre codes we need to convert
              const allCentreCodes = new Set<string>();
              counsellorsWithMissingCentres.forEach(u => {
                const key = this.normalizeName(u.userName);
                const codes = counsellorCentreMap.get(key);
                if (codes) {
                  codes.forEach(code => allCentreCodes.add(code));
                }
              });

              // Convert all centre codes to names in one batch
              const centreCodeArray = Array.from(allCentreCodes);
              const allCentres = await this.centres$.pipe(take(1)).toPromise();
              const codeToNameMap = new Map<string, string>();
              centreCodeArray.forEach(code => {
                const centre = allCentres?.find(c => c.centreCode === code);
                codeToNameMap.set(code, centre?.centreName || code);
              });

              // Now assign centres to counsellors
              for (const u of counsellorsWithMissingCentres) {
                const key = this.normalizeName(u.userName);
                const centreCodes = counsellorCentreMap.get(key);

                if (centreCodes && centreCodes.size > 0) {
                  const centreNames = Array.from(centreCodes).map(code => codeToNameMap.get(code) || code);
                  console.log(`✅ Derived centres for ${u.userName}: ${centreNames.join(', ')}`);

                  counsellors.push({
                    profileImages: u.profileImages || 'assets/img/user-avatar.png',
                    userName: u.userName || 'Unknown',
                    designation: u.jobtitle || 'Counsellor',
                    centre: centreNames,
                    disabled: u.disabled ?? false,
                    joiningDate: u.joiningDate ?? null,
                    sessionCount: 0,
                    sessionObserve: 0,
                    individualCount: 0,
                    followUpCount: 0,
                    assessmentCount: 0,
                    isSynthetic: false
                  } as Counsellor);
                }
              }
            } catch (error) {
              console.warn('⚠️ Could not fetch centres for counsellors with missing centres:', error);
            }
          }

          resolve(counsellors);
        },
        error: (err) => reject(err)
      });
    });
  }

  /**
   * ✅ Main search function using counsellingMonthlySummary (matching mis-education pattern)
   */
  async search() {
    this.loadingService.setLoading(true);

    try {
      console.log('🔍 Search initiated, counsellorsType:', this.counsellorsType);

      // ✅ Always reload employee list fresh (respects Active/All toggle)
      const counsellors = await this.loadUsersAsync();

      console.log(`📊 Loaded ${counsellors.length} ${this.counsellorsType} counsellors`);

      // ✅ Save clean snapshot so subsequent search() calls don't carry synthetics
      this.baseUsers = counsellors.map(c => ({ ...c }));
      // ✅ Restore from snapshot (zeroed counts, no leftover synthetics)
      this.allUsers = this.baseUsers.map(c => ({ ...c, sessionCount: 0, sessionObserve: 0, individualCount: 0, followUpCount: 0, assessmentCount: 0 })) as any[];

      this.removeInactiveCentres(this.allUsers);

      const [summaryDocs, assessments, observations] = await Promise.all([
        this.counselingService.getSummaryForDateRange([], this.startDate, this.endDate),
        // ✅ Use date-filtered assessment query so the Assessment card respects the selected date range.
        // Falls back to all-time when no dates are set (matching summary behaviour).
        this.counselingService.getAssessmentsListByDateRange(this.startDate, this.endDate),
        // ❌ fetchFollowUpSession() is NOT fetched here — follow-up sessions are already
        //    tracked in counsellingMonthlySummary.followUpCount (CounselingFollowUpAssessment).
        //    Counting them here again would double-count them in tableAssessmentTotal.
        // ✅ Date-filtered so the Observed card respects the selected date range (getAll() ignored dates).
        this.counsellingSessionReviewService.getObservationsByDateRange(this.startDate, this.endDate)
      ]);

      console.log(`📊 Loaded ${summaryDocs.length} summary documents`);

      // ─── Step 1: Aggregate summary docs per counsellor name ───
      const aggregated = new Map<string, { group: number; individual: number; followUp: number; observed: number; assessment: number; originalName: string }>();

      let rawIndividualSum = 0, rawGroupSum = 0, rawFollowUpSum = 0;
      const emptyNameDocs: any[] = [];   // ← capture docs with no counsellorName
      summaryDocs.forEach((doc: any) => {
        rawIndividualSum += (doc.individualCount || 0);
        rawGroupSum += (doc.groupCount || 0);
        rawFollowUpSum += (doc.followUpCount || 0);

        const key = this.normalizeName(doc.counsellorName);
        if (!key) {
          emptyNameDocs.push(doc);   // ← capture instead of only console.warn
          console.warn('⚠️ SummaryDoc with empty counsellorName skipped:', doc);
          return;
        }
        if (!aggregated.has(key)) {
          aggregated.set(key, { group: 0, individual: 0, followUp: 0, observed: 0, assessment: 0, originalName: doc.counsellorName });
        }
        const agg = aggregated.get(key)!;
        agg.group += (doc.groupCount || 0);
        agg.individual += (doc.individualCount || 0);
        agg.followUp += (doc.followUpCount || 0);
      });

      console.log('📋 RAW summaryDocs totals (before normalization/grouping):', {
        individual: rawIndividualSum,
        group: rawGroupSum,
        followUp: rawFollowUpSum,
        totalDocs: summaryDocs.length
      });

      // ─── Step 2: Count Observations directly from counsellorObservation collection ───
      (observations as any[])?.forEach((observation: any) => {
        let observerName = this.normalizeName(observation.observedBy);
        if (!observerName) return;

        if (!aggregated.has(observerName)) {
          aggregated.set(observerName, { group: 0, individual: 0, followUp: 0, observed: 0, assessment: 0, originalName: observation.observedBy });
        }
        aggregated.get(observerName)!.observed++;
      });

      // ─── Step 3: Count Assessments from real assessments collection only ───
      // Source: Counseling/allAssessments/assessments/
      // Field used: personConductingTest (AssessmentModel has no submittedBy field)
      const assessmentList: any[] = Array.isArray(assessments) ? (assessments as any[]) : [];
      console.log(`📋 Assessments loaded: ${assessmentList.length}`);
      assessmentList.forEach((assessment: any) => {
        const key = this.normalizeName(assessment.personConductingTest);
        if (!key) return;
        if (!aggregated.has(key)) {
          aggregated.set(key, { group: 0, individual: 0, followUp: 0, observed: 0, assessment: 0, originalName: assessment.personConductingTest });
        }
        aggregated.get(key)!.assessment++;
      });

      // ─── Step 3b: Merge orphan sessions → Krisha Shah ────────────────────
      // Sessions with no counsellor name, or attributed to system accounts
      // (na, admin, etc.) are reassigned to Krisha Shah's row.
      const excludedNamesForMerge = ['master admin', 'na', 'developercode', 'admin'];
      const krishaKey = this.normalizeName('Krisha Shah'); // → 'krisha shah'
      if (!aggregated.has(krishaKey)) {
        aggregated.set(krishaKey, { group: 0, individual: 0, followUp: 0, observed: 0, assessment: 0, originalName: 'Krisha Shah' });
      }
      const krishaAgg = aggregated.get(krishaKey)!;

      // A — empty-name docs (never entered aggregated)
      const orphanEmptyInd = emptyNameDocs.reduce((s: number, d: any) => s + (d.individualCount || 0), 0);
      const orphanEmptyGrp = emptyNameDocs.reduce((s: number, d: any) => s + (d.groupCount      || 0), 0);
      const orphanEmptyFu  = emptyNameDocs.reduce((s: number, d: any) => s + (d.followUpCount   || 0), 0);
      krishaAgg.individual += orphanEmptyInd;
      krishaAgg.group      += orphanEmptyGrp;
      krishaAgg.followUp   += orphanEmptyFu;

      // B — excluded-name entries: merge counts then remove from aggregated
      const mergedExcluded: { originalName: string; key: string; individual: number; group: number; followUp: number }[] = [];
      for (const exKey of excludedNamesForMerge) {
        const entry = aggregated.get(exKey);
        if (entry) {
          krishaAgg.individual += entry.individual;
          krishaAgg.group      += entry.group;
          krishaAgg.followUp   += entry.followUp;
          mergedExcluded.push({ originalName: entry.originalName, key: exKey, individual: entry.individual, group: entry.group, followUp: entry.followUp });
          aggregated.delete(exKey);
        }
      }
      console.log(`🔄 Merged orphan sessions into Krisha Shah — emptyName(ind:${orphanEmptyInd}/grp:${orphanEmptyGrp}/fu:${orphanEmptyFu}), excludedNames: [${mergedExcluded.map(m => `${m.originalName}(${m.individual})`).join(', ')}]`);
      // ─────────────────────────────────────────────────────────────────────

      // ─── Step 4: Calculate stat cards from aggregated data ───
      const summaryIndividualTotal = Array.from(aggregated.values()).reduce((sum, c) => sum + c.individual, 0);
      const summaryGroupTotal = Array.from(aggregated.values()).reduce((sum, c) => sum + c.group, 0);
      const summaryFollowUpTotal = Array.from(aggregated.values()).reduce((sum, c) => sum + c.followUp, 0);
      const summaryAssessmentTotal = Array.from(aggregated.values()).reduce((sum, c) => sum + c.assessment, 0);

      console.log('📊 AGGREGATED totals (after normalization/alias merging):', {
        individual: summaryIndividualTotal,
        group: summaryGroupTotal,
        followUp: summaryFollowUpTotal,
        assessment: summaryAssessmentTotal,
        uniqueCounsellors: aggregated.size
      });

      // Log each counsellor's aggregated counts
      console.log('👥 Per-counsellor aggregated counts:');
      aggregated.forEach((counts, key) => {
        console.log(`  ${counts.originalName} (key: ${key}) → ind:${counts.individual} grp:${counts.group} fu:${counts.followUp}`);
      });

      // ─── Step 5: Create synthetic rows for authors NOT in allUsers ───
      const excludedNames = ['master admin', 'na', 'developercode', 'admin'];

      // Build a map of counsellor name -> centres from summary docs
      const counsellorCentresMap = new Map<string, Set<string>>();
      summaryDocs.forEach((doc: any) => {
        if (!doc.counsellorName || !doc.centreCode) return;
        const key = this.normalizeName(doc.counsellorName);
        if (!counsellorCentresMap.has(key)) {
          counsellorCentresMap.set(key, new Set<string>());
        }
        counsellorCentresMap.get(key)!.add(doc.centreCode);
      });

      // Add observations to the map
      (observations as any[])?.forEach((obs: any) => {
        if (!obs.observedBy || !obs.centreCode) return;
        const key = this.normalizeName(obs.observedBy);
        if (!counsellorCentresMap.has(key)) {
          counsellorCentresMap.set(key, new Set<string>());
        }
        counsellorCentresMap.get(key)!.add(obs.centreCode);
      });

      // Convert centre codes to names
      const allCentres = await this.centres$.pipe(take(1)).toPromise();
      const convertCentreCodes = (codes: Set<string>): string[] => {
        return Array.from(codes).map(code => {
          const centre = allCentres?.find(c => c.centreCode === code);
          return centre?.centreName || code;
        });
      };

      for (const [key, counts] of aggregated) {
        if (excludedNames.includes(key)) continue;
        // ✅ Only add synthetic rows when showing "All" counsellors.
        // When "Active" is selected, only real active employees should appear.
        if (this.counsellorsType !== 'All') continue;
        const exists = this.allUsers.some(
          (u: any) => this.normalizeName(u.userName) === key
        );
        if (!exists) {
          console.log(`➕ Creating synthetic counsellor row for: ${counts.originalName}`);
          const centreCodes = counsellorCentresMap.get(key) || new Set<string>();
          const centreNames = convertCentreCodes(centreCodes);

          this.allUsers.push({
            profileImages: 'assets/images/userbig.png',
            userName: counts.originalName || key,
            designation: 'Counsellor',
            centre: centreNames.length > 0 ? centreNames : ['—'],
            disabled: false,
            sessionCount: 0,
            sessionObserve: 0,
            individualCount: 0,
            followUpCount: 0,
            assessmentCount: 0,
            isSynthetic: true
          } as any);
        }
      }

      // Remove excluded names
      this.allUsers = this.allUsers.filter(
        (u: any) => !excludedNames.includes(this.normalizeName(u.userName))
      );

      // ✅ Sort real employees before synthetics, then deduplicate by normalized name
      this.allUsers.sort((a: any, b: any) => (a.isSynthetic ? 1 : 0) - (b.isSynthetic ? 1 : 0));
      const seenNames = new Set<string>();
      this.allUsers = this.allUsers.filter((u: any) => {
        const key = this.normalizeName(u.userName);
        if (seenNames.has(key)) return false;
        seenNames.add(key);
        return true;
      });

      // ─── Step 6: Reset counts & assign from aggregated data ───
      this.allUsers.forEach((u: any) => {
        const key = this.normalizeName(u.userName);
        const counts = aggregated.get(key);
        if (counts) {
          u.sessionCount = counts.group;
          u.individualCount = counts.individual;
          u.followUpCount = counts.followUp;
          u.sessionObserve = counts.observed;
          u.assessmentCount = counts.assessment;
        } else {
          u.sessionCount = 0;
          u.individualCount = 0;
          u.followUpCount = 0;
          u.sessionObserve = 0;
          u.assessmentCount = 0;
        }
      });

      // ─── Step 7: Re-apply ordering (Krisha & Vaishali on top) ───
      this.moveKrishaAndVaishaliToTop();

      // ─── Step 8: Compute card-level totals ───
      // When "Active": sum only active employees in allUsers.
      // When "All": sum from full aggregated map (includes synthetics/inactive).
      this.tableGroupTotal = 0;
      this.tableIndividualTotal = 0;
      this.tableFollowUpTotal = 0;
      this.tableObservedTotal = 0;
      this.tableAssessmentTotal = 0;
      let excludedIndividual = 0, excludedGroup = 0, excludedFollowUp = 0;

      if (this.counsellorsType === 'Active') {
        // Only count sessions for active employees present in the table
        this.allUsers.forEach((u: any) => {
          this.tableGroupTotal += (u.sessionCount || 0);
          this.tableIndividualTotal += (u.individualCount || 0);
          this.tableFollowUpTotal += (u.followUpCount || 0);
          this.tableObservedTotal += (u.sessionObserve || 0);
          this.tableAssessmentTotal += (u.assessmentCount || 0);
        });
      } else {
        // All counsellors: use full aggregated map
        for (const [key, counts] of aggregated) {
          if (excludedNames.includes(key)) {
            excludedIndividual += counts.individual;
            excludedGroup += counts.group;
            excludedFollowUp += counts.followUp;
            console.log(`🚫 Excluded from card totals (excludedNames): ${counts.originalName} → ind:${counts.individual} grp:${counts.group}`);
            continue;
          }
          this.tableGroupTotal += counts.group;
          this.tableIndividualTotal += counts.individual;
          this.tableFollowUpTotal += counts.followUp;
          this.tableObservedTotal += counts.observed;
          this.tableAssessmentTotal += counts.assessment;
        }
      }

      console.log('🃏 CARD totals after Step 8:', {
        individual: this.tableIndividualTotal,
        group: this.tableGroupTotal,
        followUp: this.tableFollowUpTotal,
        observed: this.tableObservedTotal,
        assessment: this.tableAssessmentTotal,
        excludedIndividual,
        excludedGroup,
        excludedFollowUp
      });

      // ─── Step 8b: Build session-attribution debug report ────────────────────
      const missingLogs: MissingSessionsLog[] = [];

      // Group A — empty-name docs (merged into Krisha Shah), grouped by centre
      if (emptyNameDocs.length > 0) {
        const byCentre: { [c: string]: { ind: number; grp: number; fu: number; count: number } } = {};
        emptyNameDocs.forEach((d: any) => {
          const c = d.centreCode || 'Unknown';
          if (!byCentre[c]) byCentre[c] = { ind: 0, grp: 0, fu: 0, count: 0 };
          byCentre[c].ind   += (d.individualCount || 0);
          byCentre[c].grp   += (d.groupCount      || 0);
          byCentre[c].fu    += (d.followUpCount   || 0);
          byCentre[c].count += 1;
        });
        Object.entries(byCentre).forEach(([centreCode, v]) => {
          missingLogs.push({
            reason: 'Empty Name (→ Krisha Shah)',
            counsellorName: '(empty / null)',
            normalizedKey: '',
            individual: v.ind,
            group: v.grp,
            followUp: v.fu,
            centreCode,
            docCount: v.count,
          });
        });
      }

      // Group B — excluded-name entries (merged into Krisha Shah)
      mergedExcluded.forEach(m => {
        missingLogs.push({
          reason: 'Excluded Name (→ Krisha Shah)',
          counsellorName: m.originalName,
          normalizedKey: m.key,
          individual: m.individual,
          group: m.group,
          followUp: m.followUp,
        });
      });

      // Group C — Active mode: aggregated entries with no matching employee row
      if (this.counsellorsType === 'Active') {
        aggregated.forEach((counts, key) => {
          if (excludedNames.includes(key)) return;
          const hasRow = this.allUsers.some((u: any) => this.normalizeName(u.userName) === key);
          if (!hasRow) {
            missingLogs.push({
              reason: 'Unmatched (Active Mode)',
              counsellorName: counts.originalName,
              normalizedKey: key,
              individual: counts.individual,
              group: counts.group,
              followUp: counts.followUp,
            });
          }
        });
      }

      this.debugReport = {
        rawTotals: {
          individual: rawIndividualSum,
          group:      rawGroupSum,
          followUp:   rawFollowUpSum,
          total:      rawIndividualSum + rawGroupSum + rawFollowUpSum,
        },
        cardTotals: {
          individual: this.tableIndividualTotal,
          group:      this.tableGroupTotal,
          followUp:   this.tableFollowUpTotal,
          total:      this.tableIndividualTotal + this.tableGroupTotal + this.tableFollowUpTotal,
        },
        diff: {
          individual: rawIndividualSum       - this.tableIndividualTotal,
          group:      rawGroupSum            - this.tableGroupTotal,
          followUp:   rawFollowUpSum         - this.tableFollowUpTotal,
          total:      (rawIndividualSum + rawGroupSum + rawFollowUpSum) -
                      (this.tableIndividualTotal + this.tableGroupTotal + this.tableFollowUpTotal),
        },
        logs: missingLogs,
        generatedAt: new Date(),
      };
      // ─────────────────────────────────────────────────────────────────────────

      // ─── Debug: find aggregated entries with no matching allUsers row ───
      if (this.counsellorsType === 'All') {
        const tableRowTotal = {
          individual: this.allUsers.reduce((s: number, u: any) => s + (u.individualCount || 0), 0),
          group: this.allUsers.reduce((s: number, u: any) => s + (u.sessionCount || 0), 0),
          followUp: this.allUsers.reduce((s: number, u: any) => s + (u.followUpCount || 0), 0),
        };
        const cardVsTableDiff = {
          individual: this.tableIndividualTotal - tableRowTotal.individual,
          group: this.tableGroupTotal - tableRowTotal.group,
          followUp: this.tableFollowUpTotal - tableRowTotal.followUp,
        };
        console.log('📋 TABLE row totals vs CARD totals diff (All mode):', { tableRowTotal, cardVsTableDiff });
        if (cardVsTableDiff.individual !== 0 || cardVsTableDiff.group !== 0 || cardVsTableDiff.followUp !== 0) {
          console.warn('⚠️ MISMATCH detected! Aggregated entries NOT matched to any table row:');
          aggregated.forEach((counts, key) => {
            if (excludedNames.includes(key)) return;
            const hasRow = this.allUsers.some((u: any) => this.normalizeName(u.userName) === key);
            if (!hasRow) {
              console.warn(`  ❌ UNMATCHED: "${counts.originalName}" (key: "${key}") → ind:${counts.individual} grp:${counts.group} fu:${counts.followUp}`);
            }
          });
        } else {
          console.log('✅ Card totals match table row totals exactly.');
        }
      }

      this.dataSource.data = [...this.allUsers];
      this.loadingService.setLoading(false);

      console.log('✅ Search completed:', {
        counsellorsType: this.counsellorsType,
        totalCounsellors: this.allUsers.length,
        syntheticCounsellors: this.allUsers.filter((u: any) => u.isSynthetic).length,
        summaryDocs: summaryDocs.length,
        individualTotal: summaryIndividualTotal,
        groupTotal: summaryGroupTotal,
        followUpTotal: summaryFollowUpTotal,
        assessmentTotal: summaryAssessmentTotal
      });

    } catch (error) {
      console.error('❌ Error in search:', error);
      this.loadingService.setLoading(false);
    }
  }




  getPhoto(img?: string): string {
    return img && img.trim() !== '' ? img : 'assets/images/userbig.png';
  }

  onImageError(event: any) {
    event.target.src = 'assets/images/userbig.png';
  }

  /**
   * Get display name with "(Inactive)" suffix for synthetic or disabled users
   */
  getDisplayName(counsellor: Counsellor): string {
    const isInactive = counsellor.isSynthetic || counsellor.disabled;
    return isInactive ? `${counsellor.userName} (Inactive)` : counsellor.userName;
  }

  toggleCentre(row: Counsellor) {
    row.expanded = !row.expanded;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(null);
    this.destroyed$.complete();
  }

  formatZero(value: number): string | number {
    return value === 0 ? '-' : value;
  }

  formatJoiningDate(raw: any): string {
    if (!raw) return '—';
    let d: Date;
    if (raw?.toDate) d = raw.toDate();          // Firestore Timestamp
    else if (raw instanceof Date) d = raw;
    else d = new Date(raw);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  protected onCounsellorTypeChange($event: MatSelectChange) {
    this.counsellorsType = $event.value;
    this.search();
  }

  private removeInactiveCentres(allUsers: Employee[]) {
    const centresToRemove = ['ALBandra', 'ALGhatla', 'ALAndheri', 'ALThane'];
    allUsers.forEach((user: any) => {
      if (user.centre && Array.isArray(user.centre)) {
        user.centre = user.centre.filter((centre: string) => !centresToRemove.includes(centre));
      }
    });
    this.dataSource.data = [...allUsers];
  }

  private moveKrishaAndVaishaliToTop() {
    // Uses normalizeName so aliases ('krisha' → 'krisha shah', 'vaishali' → 'vaishali nanda') also match
    const vaishaliIndex = this.allUsers.findIndex((user: any) => this.normalizeName(user.userName) === 'vaishali nanda');
    if (vaishaliIndex > -1) {
      const vaishali = this.allUsers.splice(vaishaliIndex, 1)[0];
      this.allUsers.unshift(vaishali);
    }

    const krishaIndex = this.allUsers.findIndex((user: any) => this.normalizeName(user.userName) === 'krisha shah');
    if (krishaIndex > -1) {
      const krisha = this.allUsers.splice(krishaIndex, 1)[0];
      this.allUsers.unshift(krisha);
    }
  }

  get tableTotalSessions(): number {
    return this.tableGroupTotal + this.tableIndividualTotal + this.tableFollowUpTotal + this.tableAssessmentTotal;
  }

  get totalAttributedIndividual(): number {
    return this.debugReport?.logs.reduce((s, l) => s + l.individual, 0) ?? 0;
  }
  get totalAttributedGroup(): number {
    return this.debugReport?.logs.reduce((s, l) => s + l.group, 0) ?? 0;
  }
  get totalAttributedFollowUp(): number {
    return this.debugReport?.logs.reduce((s, l) => s + l.followUp, 0) ?? 0;
  }

  getLogRowClass(reason: string): string {
    if (reason.includes('Krisha Shah')) return 'table-info';
    if (reason === 'Unmatched (Active Mode)') return 'table-warning';
    return '';
  }

  getLogBadgeClass(reason: string): string {
    if (reason.includes('Krisha Shah')) return 'bg-info text-dark';
    if (reason === 'Unmatched (Active Mode)') return 'bg-warning text-dark';
    return 'bg-secondary';
  }
}
